import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import posts from '@/shared/api/posts';
import { BLOG_POSTS } from '@/mock';

const AllArticlesViewer = () => {
    const [loading, setLoading] = useState(false);
    const [dbArticles, setDbArticles] = useState([]);
    const [mockArticles, setMockArticles] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Показываем mock статьи сразу
        setMockArticles(BLOG_POSTS);
        
        // Загружаем статьи из базы данных
        loadDbArticles();
    }, []);

    const loadDbArticles = async () => {
        setLoading(true);
        setError(null);
        
        try {
            console.log('🔍 Loading all articles from database...');
            const response = await posts.getPosts(1, 100, true); // Все статьи, включая неопубликованные
            console.log('Database articles response:', response);
            
            if (response && response.posts) {
                setDbArticles(response.posts);
                toast.success(`Загружено ${response.posts.length} статей из базы данных`);
            } else {
                // Попробуем опубликованные статьи
                const publishedResponse = await posts.getPublishedPosts(1, 100);
                if (publishedResponse && publishedResponse.posts) {
                    setDbArticles(publishedResponse.posts);
                    toast.success(`Загружено ${publishedResponse.posts.length} опубликованных статей`);
                }
            }
        } catch (err) {
            console.error('Error loading database articles:', err);
            setError(err.message);
            toast.error('Ошибка загрузки статей из базы данных');
        } finally {
            setLoading(false);
        }
    };

    const getAllArticles = () => {
        const allSlugs = new Set();
        
        // Добавляем slug'и из базы данных
        dbArticles.forEach(article => {
            allSlugs.add(article.slug || article.id);
        });
        
        // Добавляем slug'и из mock данных
        mockArticles.forEach(article => {
            allSlugs.add(article.id);
        });
        
        return Array.from(allSlugs);
    };

    const generateCompleteSitemap = () => {
        const baseUrl = 'https://cope.by';
        const allSlugs = getAllArticles();
        
        // Статические страницы
        const staticPages = [
            { url: '/', priority: 1.0, changefreq: 'daily', lastmod: '2024-01-01' },
            { url: '/articles', priority: 0.8, changefreq: 'weekly', lastmod: '2024-01-01' },
            { url: '/tests', priority: 0.8, changefreq: 'weekly', lastmod: '2024-01-01' },
        ];

        // Все статьи
        const articleUrls = allSlugs.map(slug => ({
            url: `/article/${slug}`,
            priority: 0.9,
            changefreq: 'weekly',
            lastmod: '2024-01-01'
        }));

        // Генерируем XML
        const urls = [
            ...staticPages.map(page => formatUrl(page.url, page.priority, page.changefreq, page.lastmod)),
            ...articleUrls.map(article => formatUrl(article.url, article.priority, article.changefreq, article.lastmod))
        ];

        return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls.join('\n')}
</urlset>`;
    };

    const formatUrl = (path, priority, changefreq, lastmod) => {
        const url = `https://cope.by${path}`;
        
        return `  <url>
    <loc>${url}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>${changefreq}</changefreq>
    <priority>${priority}</priority>
  </url>`;
    };

    const copySitemap = () => {
        const sitemap = generateCompleteSitemap();
        navigator.clipboard.writeText(sitemap);
        toast.success('Полный sitemap скопирован в буфер обмена');
    };

    const allSlugs = getAllArticles();
    const completeSitemap = generateCompleteSitemap();

    return (
        <div style={{ padding: '20px', maxWidth: '1200px' }}>
            <h1>📋 Все статьи сайта</h1>
            <p>Полный обзор всех статей из разных источников</p>

            <div className="mb-4">
                <Button onClick={loadDbArticles} disabled={loading}>
                    {loading ? 'Загрузка...' : '🔄 Обновить статьи из БД'}
                </Button>
                <Button onClick={copySitemap} variant="outline" className="ml-2">
                    📋 Копировать полный sitemap
                </Button>
            </div>

            {error && (
                <Card className="mb-4 border-red-200">
                    <CardContent className="pt-4">
                        <p className="text-red-600">❌ {error}</p>
                    </CardContent>
                </Card>
            )}

            {/* Статистика */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>📊 Статистика</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{dbArticles.length}</div>
                            <div className="text-sm text-gray-600">Статей в БД</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{mockArticles.length}</div>
                            <div className="text-sm text-gray-600">Mock статей</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{allSlugs.length}</div>
                            <div className="text-sm text-gray-600">Всего уникальных</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-orange-600">{(completeSitemap.match(/<loc>/g) || []).length}</div>
                            <div className="text-sm text-gray-600">URL в sitemap</div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Статьи из базы данных */}
            {dbArticles.length > 0 && (
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>🗄️ Статьи из базы данных ({dbArticles.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-2">
                            {dbArticles.map((article, index) => (
                                <div key={index} className="p-3 border rounded bg-blue-50">
                                    <p><strong>{article.title}</strong></p>
                                    <p className="text-sm text-gray-600">
                                        Slug: {article.slug || article.id}<br/>
                                        URL: /article/{article.slug || article.id}<br/>
                                        Статус: {article.published ? '✅ Опубликовано' : '❌ Черновик'}<br/>
                                        Создана: {article.createdAt ? new Date(article.createdAt).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Mock статьи */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>🧪 Mock статьи ({mockArticles.length})</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-2">
                        {mockArticles.map((article, index) => (
                            <div key={index} className="p-3 border rounded bg-green-50">
                                <p><strong>{article.title}</strong></p>
                                <p className="text-sm text-gray-600">
                                    Slug: {article.id}<br/>
                                    URL: /article/{article.id}<br/>
                                    Дата: {article.date}
                                </p>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Полный sitemap */}
            <Card>
                <CardHeader>
                    <CardTitle>🌐 Полный sitemap ({allSlugs.length} статей)</CardTitle>
                </CardHeader>
                <CardContent>
                    <details open>
                        <summary>Показать полный XML</summary>
                        <div className="mt-4">
                            <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-96 whitespace-pre-wrap font-mono border">
                                {completeSitemap}
                            </pre>
                        </div>
                    </details>
                </CardContent>
            </Card>
        </div>
    );
};

export default AllArticlesViewer;
